// src/components/resources/tools/generators/CommentThread/YouTubeComments.tsx

'use client'

import type { FC } from 'react'
import { Heart, Pin, ThumbsDown, ThumbsUp } from 'lucide-react'
import { clsx } from 'clsx'
import {
  avatarInitial,
  countComments,
  displayedLikes,
  formatCompactCount,
  formatHandle,
  type ThreadComment,
} from './threadModel'
import { CommentAvatar, likeButtonLabel, VerifiedCheck, type CommentPreviewProps } from './previewShared'

/**
 * YouTube comments panel, styled to the current watch-page UI: "Comments"
 * header with the total count, `@handle` bylines with a relative timestamp,
 * thumbs-up count + thumbs-down + Reply actions, and one indent level of
 * replies. Creator comments render YouTube's highlighted handle pill; pinned
 * comments show the "Pinned by …" line above the byline. The thumbs-up is a
 * real toggle (aria-pressed, filled when liked, count +1), and a comment can
 * carry YouTube's "hearted by creator" badge — the creator's mini avatar with
 * the red heart, rendered after the action row like the real panel.
 */
export const YouTubeComments: FC<CommentPreviewProps> = ({ comments, postAuthor, theme, onToggleLike }) => {
  const isDark = theme === 'dark'
  const text = isDark ? 'text-[#f1f1f1]' : 'text-[#0f0f0f]'
  const muted = isDark ? 'text-[#aaaaaa]' : 'text-[#606060]'
  const border = isDark ? 'border-[#3f3f3f]' : 'border-gray-200'
  const total = countComments(comments)

  const renderRow = (comment: ThreadComment, isReply = false): JSX.Element => (
    <div className={clsx('flex gap-3 py-2.5', isReply && 'pl-11')}>
      <CommentAvatar username={comment.username} size={isReply ? 'sm' : 'md'} flavor="youtube" />
      <div className="min-w-0 flex-1">
        {comment.pinned && !isReply && (
          <p className={clsx('mb-0.5 flex items-center gap-1 text-[11px]', muted)}>
            <Pin aria-hidden="true" className="h-3 w-3" />
            Pinned by {formatHandle(postAuthor)}
          </p>
        )}
        <p className="flex flex-wrap items-center gap-1.5">
          <span
            className={clsx(
              'inline-flex items-center gap-1 text-[13px] font-medium',
              comment.byAuthor
                ? clsx('rounded-full px-1.5 py-px', isDark ? 'bg-[#3f3f3f] text-[#f1f1f1]' : 'bg-gray-200 text-[#0f0f0f]')
                : text,
            )}
          >
            {formatHandle(comment.username)}
            {comment.verified && <VerifiedCheck className={muted} />}
          </span>
          <span className={clsx('text-[12px]', muted)}>{comment.timestamp}</span>
        </p>
        <p className={clsx('mt-0.5 whitespace-pre-wrap text-[14px] leading-snug', text)}>{comment.text}</p>
        <p className={clsx('mt-1.5 flex items-center gap-4 text-[12px]', muted)}>
          <button
            type="button"
            onClick={() => onToggleLike?.(comment.id)}
            aria-pressed={comment.liked}
            aria-label={likeButtonLabel(comment.liked, comment.username)}
            className="flex items-center gap-1 rounded p-0.5 transition-transform hover:scale-110"
          >
            <ThumbsUp className={clsx('h-3.5 w-3.5', comment.liked && clsx('fill-current', text))} />
            {displayedLikes(comment) > 0 && <span>{formatCompactCount(displayedLikes(comment))}</span>}
          </button>
          <ThumbsDown aria-hidden="true" className="h-3.5 w-3.5" />
          {comment.creatorHearted && (
            // "Hearted by creator": the creator's mini avatar with the small
            // red heart badge, as the real panel renders under the actions.
            <span className="relative inline-flex" role="img" aria-label={`Hearted by ${formatHandle(postAuthor)}`}>
              <span className="flex h-4 w-4 items-center justify-center rounded-full bg-gradient-to-br from-red-500 to-rose-600 text-[9px] font-bold text-white">
                {avatarInitial(postAuthor)}
              </span>
              <Heart aria-hidden="true" className="absolute -bottom-1 -right-1 h-2.5 w-2.5 fill-[#ff0000] text-[#ff0000]" />
            </span>
          )}
          <span className="font-medium">Reply</span>
        </p>
      </div>
    </div>
  )

  return (
    <div
      className={clsx(
        'overflow-hidden rounded-xl border px-4 py-3 shadow-xl',
        border,
        isDark ? 'bg-[#0f0f0f]' : 'bg-white',
      )}
    >
      <p className={clsx('flex items-baseline gap-2 pb-1 text-[16px] font-bold', text)}>
        Comments
        <span className={clsx('text-[13px] font-normal', muted)}>{formatCompactCount(total)}</span>
      </p>
      {comments.map(comment => (
        <div key={comment.id}>
          {renderRow(comment)}
          {comment.replies.map(reply => (
            <div key={reply.id}>{renderRow(reply, true)}</div>
          ))}
        </div>
      ))}
    </div>
  )
}
